// ----- chPopover Constants ---------------------------------------------------


/**
 * Event to braodcast to child scopes when a popover is closed.
 *
 * @type {string}
 */
export const POPOVER_CLOSE_EVENT = 'chPopoverClose';


/**
 * Base class applied to popover wrapper elements.
 *
 * @type {string}
 */
export const POPOVER_CLASS = 'chPopover';


/**
 * Class to apply to the popover's when it becomes active. Useful for animatins.
 *
 * @type {string}
 */
export const POPOVER_ACTIVE_CLASS = 'chPopover--active';


/**
 * Class applied to the popover wrapper element.
 *
 * @type {string}
 */
export const WRAPPER_CLASS = 'chPopover__wrapper';


/**
 * Base template into which popover content will be appended.
 *
 * @type {string}
 */
export const POPOVER_TEMPLATE = `<aside class="${WRAPPER_CLASS}"></aside>`;


/**
 * Class applied to the arrow element inside popovers.
 *
 * @type {string}
 */
export const ARROW_CLASS = 'chPopover__arrow';


/**
 * Template used for popovers' arrows.
 *
 * @type {string}
 */
export const ARROW_TEMPLATE = `<arrow class="${ARROW_CLASS}"></arrow>`;


/**
 * Amount to add or subtract (depending on side) from the arrow offset to
 * correct for subpixel rounding errors that can cause display bugs.
 *
 * @type {number}
 */
export const ARROW_OFFSET_ADJUSTMENT = 1;


/**
 * List of known triggers and their corresponding show/hide events.
 *
 * @type {object}
 */
export const TRIGGERS = {
  hover: {
    show: ['mouseenter', 'touchstart'],
    hide: ['mouseleave', 'touchstart']
  },
  click: {
    show: ['click'],
    hide: ['click', 'touchstart']
  },
  focus: {
    show: ['focus'],
    hide: ['blur']
  }
};


/**
 * Of the 81 possible attachment combinations, only 28 guarantee edge overlap.
 * While it is possible to configure a popover without overlap, in most cases
 * this is likely a developer error, so a warning will be displayed in
 * development environments.
 *
 * @type {array}
 */
export const EDGE_OVERLAP_ATTACHMENTS = [
  // Valid "top left" popover attachments.
  ['top left', 'bottom left'],
  ['top left', 'bottom center'],
  ['top left', 'middle right'],
  ['top left', 'top right'],

  // Valid "top center" popover attachments.
  ['top center', 'bottom left'],
  ['top center', 'bottom center'],
  ['top center', 'bottom right'],

  // Valid "top right" popover attachments.
  ['top right', 'top left'],
  ['top right', 'middle left'],
  ['top right', 'bottom center'],
  ['top right', 'bottom right'],

  // Valid "middle left" attachments.
  ['middle left', 'top right'],
  ['middle left', 'middle right'],
  ['middle left', 'bottom right'],

  // Valid "middle right" attachments.
  ['middle right', 'top left'],
  ['middle right', 'middle left'],
  ['middle right', 'bottom left'],

  // Valid "bottom left" attachments.
  ['bottom left', 'top left'],
  ['bottom left', 'top center'],
  ['bottom left', 'middle right'],
  ['bottom left', 'bottom right'],

  // Valid "bottom center" attachments.
  ['bottom center', 'top left'],
  ['bottom center', 'top center'],
  ['bottom center', 'top right'],

  // Valid "bottom right" attachments.
  ['bottom right', 'bottom left'],
  ['bottom right', 'middle left'],
  ['bottom right', 'top center'],
  ['bottom right', 'top right']
];


/**
 * List of self-closing tags, which are invalid hosts for popovers.
 *
 * @type {array}
 */
export const SELF_CLOSING_TAGS = [
  'AREA',
  'BASE',
  'BR',
  'COL',
  'COMMAND',
  'EMBED',
  'HR',
  'IMG',
  'INPUT',
  'KEYGEN',
  'LINK',
  'META',
  'PARAM',
  'SOURCE',
  'TRACK',
  'WBR'
];


/**
 * Default options that will be used if the consumer does not provide a value
 * for them.
 *
 * @type {object}
 */
export const DEFAULT_OPTIONS = {
  trigger: 'hover',
  attachment: 'bottom center',
  targetAttachment: 'top center',
  offset: '0 0',
  targetOffset: '0 0',
  constraints: [],
  hideGracePeriod: 0,
  documentCloseEvents: true
};


/**
 * Mapping of sides to their relevant box dimension, arrow offset axis, and
 * popover offset axis.
 *
 * @type {array}
 */
export const SIDES = {
  top: ['height', 'left', 'top'],
  bottom: ['height', 'left', 'top'],
  right: ['width', 'top', 'left'],
  left: ['width', 'top', 'left']
};



// WEBPACK FOOTER //
// ./etc/constants.js